import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, Ic, ICONS, TranslateToggle } from "../components/UI.jsx";
import { useChatStore, useAuthStore } from "../context/store.js";
import { getSocket } from "../hooks/useSocket.js";
import { formatDistanceToNow } from "date-fns";

const B = {
  bg:"#08070F",bg2:"#0D0C18",bg3:"#131124",
  violet:"#7C3AED",violetLt:"#8B5CF6",
  violetDim:"rgba(124,58,237,0.15)",violetBorder:"rgba(124,58,237,0.3)",
  vibeGreen:"#10F5A0",vibeGreenDim:"rgba(16,245,160,0.12)",
  coral:"#FF6B6B",coralDim:"rgba(255,107,107,0.12)",
  sky:"#38BDF8",skyDim:"rgba(56,189,248,0.12)",
  text:"#F0EFFE",text2:"#7C7A9E",text3:"#3D3B5C",
  border:"rgba(124,58,237,0.15)",border2:"rgba(124,58,237,0.08)",
  grad:"linear-gradient(135deg,#7C3AED,#10F5A0)",
  font:"'Sora',system-ui,sans-serif",mono:"'Space Mono',monospace",
};

const timeAgo = d => { try { return formatDistanceToNow(new Date(d),{addSuffix:true}); } catch { return ""; } };

export default function ChatPage({ onCall }) {
  const navigate = useNavigate();
  const { user }     = useAuthStore();
  const { chats, activeChat, messages, openChat, sendMessage, typing } = useChatStore();
  const [input,     setInput]     = useState("");
  const [sending,   setSending]   = useState(false);
  const [transOn,   setTransOn]   = useState(false);
  const [transLang, setTransLang] = useState("Spanish");
  const [q, setQ] = useState("");
  const scrollRef = useRef(null);
  const inputRef  = useRef(null);

  // Use real API chats from database only
  const displayChats = chats;

  const filteredChats = displayChats.filter(c =>
    !q.trim() || c.name?.toLowerCase().includes(q.toLowerCase())
  );

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const handleOpenChat = (chat) => {
    openChat(chat);
    // Join socket room
    const socket = getSocket();
    if (socket && chat._id) {
      socket.emit("chat:join", chat._id);
    }
    inputRef.current?.focus();
  };

  const handleSend = async () => {
    if (!input.trim() || !activeChat) return;
    const text = input.trim();
    setInput("");

    setSending(true);
    try { 
      await sendMessage(text); 
    } catch {}
    setSending(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const chatUser = activeChat?.type === "dm" ? activeChat?.participants?.find(p => p._id !== user?._id) || activeChat?.participants?.[0] : null;
  const chatColor = activeChat?.color || chatUser?.color || B.violet;

  // Real-time typers in active chat
  const typers = activeChat
    ? Object.entries(typing)
        .filter(([key, u]) => key.startsWith(`${activeChat._id}:`) && u && u._id !== user?._id)
        .map(([_, u]) => u)
    : [];

  return (
    <div style={{ display:"flex",height:"100%",overflow:"hidden" }}>

      {/* ── Chat list ── */}
      <div style={{ width:340,borderRight:`1px solid ${B.border2}`,display:"flex",flexDirection:"column",flexShrink:0 }}>
        <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 16px",borderBottom:`1px solid ${B.border2}` }}>
          <span style={{ fontWeight:900,fontSize:22,color:B.text }}>Connect</span>
          <button style={{ width:34,height:34,borderRadius:"50%",border:"none",background:B.violetDim,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}>
            <Ic d={ICONS.edit} s={16} c={B.violetLt} />
          </button>
        </div>

        {/* Search */}
        <div style={{ padding:"8px 12px",borderBottom:`1px solid ${B.border2}` }}>
          <div style={{ display:"flex",alignItems:"center",gap:8,background:B.bg3,borderRadius:10,padding:"8px 14px",border:`1px solid ${B.border2}` }}>
            <Ic d={ICONS.search} s={16} c={B.text2} />
            <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search conversations…"
              style={{ flex:1,background:"none",border:"none",color:B.text,fontFamily:B.font,fontSize:14,outline:"none" }} />
          </div>
        </div>

        {/* Chat items */}
        <div style={{ flex:1,overflowY:"auto" }}>
          {filteredChats.length > 0 ? (
            filteredChats.map(chat => {
              const u    = chat.type==="dm" ? chat.participants?.find(p=>p._id!==user?._id)||chat.participants?.[0] : null;
              const color = u?.color || chat.color || B.violet;
              const isActive = activeChat?._id === chat._id;
              return (
                <div key={chat._id} onClick={()=>handleOpenChat(chat)} style={{
                  display:"flex",gap:10,padding:"13px 16px",cursor:"pointer",
                  borderBottom:`1px solid ${B.border2}`,alignItems:"flex-start",
                  background:isActive?B.violetDim:"none",transition:"background 0.15s",
                  borderLeft:isActive?`3px solid ${B.violet}`:"3px solid transparent",
                }}
                  onMouseEnter={e=>{if(!isActive)e.currentTarget.style.background="rgba(124,58,237,0.06)";}}
                  onMouseLeave={e=>{if(!isActive)e.currentTarget.style.background="none";}}>
                  <div style={{ position:"relative",flexShrink:0 }}>
                    {u ? (
                      <Avatar user={u} size={48} />
                    ) : (
                      <div style={{ width:48,height:48,borderRadius:"50%",background:`${color}18`,border:`1.5px solid ${color}30`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20 }}>👥</div>
                    )}
                    {u?.online && <div style={{ position:"absolute",bottom:0,right:0,width:13,height:13,borderRadius:"50%",background:B.vibeGreen,border:`2px solid ${B.bg}`,boxShadow:`0 0 6px rgba(16,245,160,0.6)` }} />}
                  </div>
                  <div style={{ flex:1,minWidth:0 }}>
                    <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center" }}>
                      <span style={{ fontWeight:700,fontSize:15,color:B.text }}>{chat.name||u?.displayName||"Chat"}</span>
                      <span style={{ fontSize:11,color:B.text2,fontFamily:B.mono,flexShrink:0 }}>{timeAgo(chat.updatedAt)}</span>
                    </div>
                    <div style={{ fontSize:13,color:B.text2,marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>
                      {chat.lastMessage?.content || chat.preview || "Start a conversation"}
                    </div>
                  </div>
                  {chat.unread > 0 && (
                    <div style={{ width:20,height:20,borderRadius:"50%",background:B.grad,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:"#fff",fontWeight:700,flexShrink:0,boxShadow:"0 2px 8px rgba(124,58,237,0.4)" }}>{chat.unread}</div>
                  )}
                </div>
              );
            })
          ) : (
            <div style={{ padding: "48px 24px", textAlign: "center", color: B.text2 }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>💬</div>
              <div style={{ fontWeight: 700, fontSize: 16, color: B.text, marginBottom: 6 }}>No Active Chats</div>
              <p style={{ fontSize: 13, lineHeight: 1.6, margin: "0 0 16px 0" }}>
                Go to the Explore tab to find other members, connect, and start vibing!
              </p>
              <button 
                onClick={() => navigate("/explore")}
                style={{
                  padding: "10px 20px", borderRadius: 9999, border: "none",
                  background: B.grad, color: "#fff", fontFamily: B.font,
                  fontWeight: 700, fontSize: 14, cursor: "pointer",
                  boxShadow: "0 4px 14px rgba(124,58,237,0.35)", transition: "all 0.2s"
                }}
                onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
                onMouseLeave={e => e.currentTarget.style.transform = "none"}
              >
                Explore Vibers ✦
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Message thread ── */}
      {activeChat ? (
        <div style={{ flex:1,display:"flex",flexDirection:"column",overflow:"hidden" }}>
          {/* Chat header */}
          <div style={{ display:"flex",alignItems:"center",gap:12,padding:"12px 16px",borderBottom:`1px solid ${B.border2}`,flexShrink:0 }}>
            {activeChat.type==="dm"&&chatUser ? (
              <Avatar user={chatUser} size={38} showOnline />
            ) : (
              <div style={{ width:38,height:38,borderRadius:"50%",background:`${chatColor}18`,border:`1.5px solid ${chatColor}30`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18 }}>👥</div>
            )}
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:700,fontSize:15,color:B.text }}>{activeChat.name||chatUser?.displayName}</div>
              {chatUser?.online && <div style={{ color:B.vibeGreen,fontSize:12 }}>● Online now</div>}
              {activeChat.type==="group" && <div style={{ color:B.text2,fontSize:12 }}>{activeChat.participants?.length||2} members</div>}
            </div>
            <div style={{ display:"flex",gap:4,alignItems:"center" }}>
              <TranslateToggle on={transOn} onToggle={()=>setTransOn(v=>!v)} lang={transLang} onLang={setTransLang} />
              {activeChat.type==="dm"&&chatUser&&(
                <>
                  <button onClick={()=>onCall?.({type:"voice",user:chatUser})} style={{ width:36,height:36,borderRadius:"50%",border:"none",background:B.vibeGreenDim,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}>
                    <Ic d={ICONS.phone} s={16} c={B.vibeGreen} />
                  </button>
                  <button onClick={()=>onCall?.({type:"video",user:chatUser})} style={{ width:36,height:36,borderRadius:"50%",border:"none",background:B.skyDim,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}>
                    <Ic d={ICONS.video} s={16} c={B.sky} />
                  </button>
                </>
              )}
              {activeChat.type==="group"&&(
                <button onClick={()=>onCall?.({type:"group",name:activeChat.name})} style={{ padding:"7px 16px",borderRadius:9999,border:`1px solid ${B.violetBorder}`,background:B.violetDim,color:B.violetLt,fontFamily:B.font,fontWeight:700,fontSize:13,cursor:"pointer" }}>Join Call</button>
              )}
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} style={{ flex:1,overflowY:"auto",padding:"16px",display:"flex",flexDirection:"column",gap:10 }}>
            {messages.map((msg,i) => {
              const isMe = msg.from==="me" || msg.sender?._id===user?._id || msg.sender===user?._id;
              const senderName = isMe ? "You" : (msg.sender?.displayName || chatUser?.displayName || "Them");
              const senderUser = isMe ? user : (msg.sender || chatUser || activeChat?.participants?.[0]);
              return (
                <div key={msg._id||i} style={{ display:"flex",justifyContent:isMe?"flex-end":"flex-start",gap:8,alignItems:"flex-end" }}>
                  {!isMe && <Avatar user={senderUser} size={28} />}
                  <div style={{ maxWidth:"70%" }}>
                    {!isMe && activeChat.type==="group" && (
                      <div style={{ color:senderUser?.color||B.text2,fontSize:11,marginBottom:3,fontFamily:B.mono }}>{senderName}</div>
                    )}
                    <div style={{
                      padding:"11px 15px",fontSize:14,lineHeight:1.5,
                      borderRadius:isMe?"18px 18px 4px 18px":"18px 18px 18px 4px",
                      background:isMe?B.grad:B.bg3,
                      color:"#fff",
                      boxShadow:isMe?"0 4px 16px rgba(124,58,237,0.3)":"none",
                      wordBreak:"break-word",
                    }}>{msg.content}</div>
                    {transOn&&!isMe&&<div style={{ color:B.violetLt,fontSize:11,fontStyle:"italic",marginTop:3,paddingLeft:4 }}>↳ [{transLang}] Translation available…</div>}
                    <div style={{ fontSize:10,color:B.text3,marginTop:3,textAlign:isMe?"right":"left",fontFamily:B.mono }}>
                      {msg.createdAt ? new Date(msg.createdAt).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}) : "now"}
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* Real-time typing indicators */}
            {typers.map((typer, idx) => (
              <div key={idx} style={{ display:"flex",gap:8,alignItems:"flex-end" }}>
                <Avatar user={typer} size={28} />
                <div style={{ padding:"10px 14px",borderRadius:"18px 18px 18px 4px",background:B.bg3 }}>
                  <div style={{ display:"flex",gap:4 }}>
                    {[0,1,2].map(j=><div key={j} style={{ width:6,height:6,borderRadius:"50%",background:B.text2,animation:`pulse-dot 1.2s ${j*0.2}s ease-in-out infinite` }} />)}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Input */}
          <div style={{ display:"flex",gap:10,padding:"12px 16px",borderTop:`1px solid ${B.border2}`,alignItems:"flex-end",flexShrink:0 }}>
            <button style={{ width:36,height:36,borderRadius:"50%",border:"none",background:B.violetDim,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
              <Ic d={ICONS.plus} s={16} c={B.violetLt} />
            </button>
            <textarea
              ref={inputRef}
              value={input} onChange={e=>setInput(e.target.value)} onKeyDown={handleKeyDown}
              placeholder={`Message ${activeChat.name||chatUser?.displayName||""}…`}
              rows={1}
              style={{ flex:1,background:B.bg3,border:`1px solid ${B.border}`,borderRadius:20,padding:"10px 16px",color:B.text,fontFamily:B.font,fontSize:14,outline:"none",resize:"none",maxHeight:120,lineHeight:1.5 }}
              onInput={e=>{ e.target.style.height="auto"; e.target.style.height=Math.min(e.target.scrollHeight,120)+"px"; }}
            />
            <button onClick={handleSend} disabled={!input.trim()||sending} style={{
              width:38,height:38,borderRadius:"50%",border:"none",cursor:input.trim()&&!sending?"pointer":"default",
              background:input.trim()&&!sending?B.grad:`${B.violet}30`,display:"flex",alignItems:"center",justifyContent:"center",
              boxShadow:input.trim()&&!sending?"0 4px 14px rgba(124,58,237,0.4)":"none",transition:"all 0.2s",flexShrink:0,
            }}>
              {sending ? <div className="vyl-spinner" style={{ width:14,height:14 }} /> : <Ic d={ICONS.send} s={15} c="#fff" />}
            </button>
          </div>
        </div>
      ) : (
        /* Empty state */
        <div style={{ flex:1,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:14 }}>
          <div style={{ width:80,height:80,borderRadius:"50%",background:B.violetDim,border:`1px solid ${B.violetBorder}`,display:"flex",alignItems:"center",justifyContent:"center" }}>
            <Ic d={ICONS.chat} s={38} c={B.violetLt} />
          </div>
          <div style={{ fontWeight:900,fontSize:24,color:B.text }}>Your Connections</div>
          <div style={{ color:B.text2,fontSize:15 }}>Select a conversation to start vibing</div>
        </div>
      )}
    </div>
  );
}
